import React, { useState, useEffect, useCallback, useTransition, Suspense, lazy } from "react";
import Header from "@/reusable_sections/Header";
import Footer from "@/reusable_sections/Footer";
import { CosmicBackground } from "@/components/cosmic/CosmicBackground";
import { MISSIONS, type MissionId } from "@/lib/simulation/missions";
import { useSimProgress } from "@/lib/simulation/progress";
import { scrollToElementAfterRender, scrollToElement } from "@/lib/scroll";
import type { MissionProps } from "./shared/types";

// Page styles
import "./simulation.css";

// Page sections
import Opening from "./sections/Opening";
import ControlCenter from "./sections/ControlCenter";

// Lazy-loaded missions from EXACT paths defined in docs/wvc-batch-spec.md
const RealityTest = lazy(() => import("./missions/RealityTest"));
const Observer = lazy(() => import("./missions/Observer"));
const Laws = lazy(() => import("./missions/Laws"));
const QuantumQuestion = lazy(() => import("./missions/QuantumQuestion"));
const ComputationalUniverse = lazy(() => import("./missions/ComputationalUniverse"));
const SimulationArgument = lazy(() => import("./missions/SimulationArgument"));
const SearchForEvidence = lazy(() => import("./missions/SearchForEvidence"));
const BuildUniverse = lazy(() => import("./missions/BuildUniverse"));
const ConsciousnessProblem = lazy(() => import("./missions/ConsciousnessProblem"));
const FinalVerdict = lazy(() => import("./missions/FinalVerdict"));
const References = lazy(() => import("./missions/References"));
const MISSION_COMPONENTS: Record<MissionId, React.LazyExoticComponent<React.ComponentType<MissionProps>>> = {
  reality: RealityTest,
  observer: Observer,
  laws: Laws,
  quantum: QuantumQuestion,
  computational: ComputationalUniverse,
  argument: SimulationArgument,
  evidence: SearchForEvidence,
  build: BuildUniverse,
  consciousness: ConsciousnessProblem,
  verdict: FinalVerdict
};
const VALID_MISSION_IDS = new Set<string>(MISSIONS.map(m => m.id));
interface MissionErrorBoundaryProps {
  missionId: MissionId;
  children: React.ReactNode;
}
interface MissionErrorBoundaryState {
  hasError: boolean;
}
class MissionErrorBoundary extends React.Component<MissionErrorBoundaryProps, MissionErrorBoundaryState> {
  constructor(props: MissionErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false
    };
  }
  static getDerivedStateFromError() {
    return {
      hasError: true
    };
  }
  componentDidCatch(error: unknown) {
    // Graceful error logging without spam
    if ((import.meta as unknown as {
      env?: {
        DEV?: boolean;
      };
    }).env?.DEV) {
      console.warn("Mission component failed to load:", error);
    }
  }
  componentDidUpdate(prevProps: MissionErrorBoundaryProps) {
    if (prevProps.missionId !== this.props.missionId && this.state.hasError) {
      this.setState({
        hasError: false
      });
    }
  }
  render() {
    if (this.state.hasError) {
      return <div className="w-full max-w-2xl mx-auto my-12 p-8 rounded-xl border border-destructive/40 bg-card/60 backdrop-blur-md text-center">
          <h3 className="font-serif text-xl font-bold text-destructive mb-2">This mission could not load</h3>
          <p className="font-sans text-sm text-muted-foreground mb-6">
            The module is temporarily unavailable or still being initialized. You can retry or explore another mission.
          </p>
          <button type="button" onClick={() => this.setState({
          hasError: false
        })} className="px-6 py-2.5 rounded-full border border-primary bg-primary text-primary-foreground font-serif text-sm font-semibold hover:brightness-110 min-h-[44px]">
            Retry Mission
          </button>
        </div>;
    }
    return this.props.children;
  }
}
function MissionSkeleton() {
  return <div className="w-full max-w-4xl mx-auto my-12 p-8 rounded-xl border border-border/40 bg-card/40 backdrop-blur-md animate-pulse">
      <div className="w-28 h-6 rounded-full bg-muted/60 mb-4" />
      <div className="w-3/4 h-10 rounded-md bg-muted/80 mb-4" />
      <div className="w-full h-4 rounded bg-muted/50 mb-2" />
      <div className="w-2/3 h-4 rounded bg-muted/50 mb-8" />
      <div className="w-full h-64 rounded-lg bg-muted/30" />
    </div>;
}
export default function SimulationTheoryPage() {
  const {
    completed
  } = useSimProgress();
  const [, startTransition] = useTransition();

  // Parse initial URL query parameters (?m=<id> or ?b=<anything>)
  const [initialParams] = useState<{
    missionId: MissionId;
    shouldSkipIntro: boolean;
  }>(() => {
    if (typeof window === "undefined") {
      return {
        missionId: "reality",
        shouldSkipIntro: false
      };
    }
    const searchParams = new URLSearchParams(window.location.search);
    const mParam = searchParams.get("m");
    const bParam = searchParams.get("b");
    const sessionEntered = window.sessionStorage?.getItem("ql.simulation.entered") === "true";

    // Build-a-universe share link skips intro and opens 'build' directly
    if (bParam !== null) {
      return {
        missionId: "build",
        shouldSkipIntro: true
      };
    }
    if (mParam && VALID_MISSION_IDS.has(mParam)) {
      return {
        missionId: mParam as MissionId,
        shouldSkipIntro: true
      };
    }
    return {
      missionId: "reality",
      shouldSkipIntro: sessionEntered
    };
  });

  // State: has the user entered the investigation?
  const [hasEntered, setHasEntered] = useState<boolean>(initialParams.shouldSkipIntro);

  // State: current active mission id
  const [currentMissionId, setCurrentMissionId] = useState<MissionId>(() => {
    if (initialParams.missionId) return initialParams.missionId;
    const firstUnfinished = MISSIONS.find(m => !completed.includes(m.id));
    return firstUnfinished ? firstUnfinished.id : "reality";
  });

  // Keep ?m=<id> in sync via history.replaceState, preserving ?b= and other query parameters
  const updateUrlMission = useCallback((id: MissionId) => {
    if (typeof window === "undefined") return;
    try {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set("m", id);
      const newQuery = searchParams.toString();
      const newUrl = `${window.location.pathname}${newQuery ? `?${newQuery}` : ""}${window.location.hash}`;
      window.history.replaceState(null, "", newUrl);
    } catch {
      // Ignore
    }
  }, []);
  const switchMission = useCallback((id: MissionId, autoScroll = true) => {
    startTransition(() => {
      setCurrentMissionId(id);
    });
    updateUrlMission(id);
    if (autoScroll) {
      scrollToElementAfterRender(() => document.getElementById(`sim-mission-${id}`) || document.getElementById("sim-current-mission-anchor"));
    }
  }, [updateUrlMission]);
  const handleEnter = useCallback(() => {
    setHasEntered(true);
    if (typeof window !== "undefined") {
      try {
        window.sessionStorage?.setItem("ql.simulation.entered", "true");
      } catch {
        // Ignore
      }
    }
    updateUrlMission(currentMissionId);
    scrollToElementAfterRender(() => document.getElementById("sim-control-center-anchor"));
  }, [currentMissionId, updateUrlMission]);
  const handleReplayIntro = useCallback(() => {
    setHasEntered(false);
    if (typeof window !== "undefined") {
      try {
        window.sessionStorage?.removeItem("ql.simulation.entered");
      } catch {
        // Ignore
      }
      scrollToElement(document.getElementById("sim-opening-anchor"), {
        behavior: "smooth"
      });
    }
  }, []);

  // Compute navigation handlers for current mission
  const currentMissionIndex = MISSIONS.findIndex(m => m.id === currentMissionId);
  const prevDef = currentMissionIndex > 0 ? MISSIONS[currentMissionIndex - 1] : null;
  const nextDef = currentMissionIndex < MISSIONS.length - 1 ? MISSIONS[currentMissionIndex + 1] : null;
  const handlePrev = prevDef ? () => switchMission(prevDef.id, true) : undefined;
  const handleNext = nextDef ? () => switchMission(nextDef.id, true) : undefined;
  const handleGoTo = (id: MissionId) => switchMission(id, true);
  const handleExploreScience = () => {
    scrollToElement("sim-references", {
      behavior: "smooth"
    });
  };
  const CurrentMissionComponent = MISSION_COMPONENTS[currentMissionId];
  return <div className="sim-page relative min-h-[100svh] w-full max-w-full overflow-x-clip bg-transparent text-foreground">
      {/* Root CosmicBackground with blue accent behind the whole page */}
      <CosmicBackground fixed accent="blue" density={1} />

      {/* Page content layer */}
      <div className="relative z-10 flex flex-col min-h-[100svh] w-full max-w-full min-w-0">
        <Header />

        <main className="flex-grow w-full max-w-full min-w-0">
          {/* Opening intro section */}
          {!hasEntered && <div id="sim-opening-anchor">
              <Opening onEnter={handleEnter} />
            </div>}

          {/* Investigation Control Center (visible once entered) */}
          {hasEntered && <div id="sim-control-center-anchor" className="w-full">
              <ControlCenter currentMissionId={currentMissionId} onSelectMission={id => switchMission(id, true)} onReplayIntro={handleReplayIntro} />

              {/* Mission Presentation Stage */}
              <div id="sim-current-mission-anchor" className="w-full max-w-full">
                <MissionErrorBoundary missionId={currentMissionId}>
                  <Suspense fallback={<MissionSkeleton />}>
                    <CurrentMissionComponent onPrev={handlePrev} onNext={handleNext} goTo={handleGoTo} onExploreScience={handleExploreScience} />
                  </Suspense>
                </MissionErrorBoundary>
              </div>

              {/* Scientific references section below the missions */}
              <div id="sim-references" className="w-full max-w-full">
                <Suspense fallback={<div className="h-20" />}>
                  <References />
                </Suspense>
              </div>
            </div>}
        </main>

        <Footer />
      </div>
    </div>;
}